import React from 'react';

import {
  Button,
  Divider,
  Grid,
  Group,
  LoadingOverlay,
  Paper,
  px,
  Stack,
  Text,
  TextInput,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure, useShallowEffect } from '@mantine/hooks';
import useUpdateGym from 'hooks/gym/useUpdateGym';
import { GymMailConfigWrite, GymRetrieve } from 'types';
import { emailValidator, replyToEmailValidator, senderNameValidator } from 'utils/form-validators';
import notificationHandler from 'utils/notifications';

interface FormValues {
  gymMailConfig: GymMailConfigWrite;
}

const hasAnyMailgunField = (values: FormValues): boolean => {
  const { fromEmail, fromName, replyTo } = values.gymMailConfig || {};
  return !!fromEmail || !!fromName || !!replyTo;
};

const validateGymMailConfigFromName = (value: string | null, values: FormValues): string | null => {
  if (hasAnyMailgunField(values)) {
    return senderNameValidator(value);
  }
  return null;
};
const validateGymMailConfigEmail = (value: string | null, values: FormValues): string | null => {
  if (hasAnyMailgunField(values)) {
    return emailValidator(value);
  }
  return null;
};
const validateGymMailConfigReplyToEmail = (value: string | null, values: FormValues): string | null => {
  if (hasAnyMailgunField(values)) {
    return replyToEmailValidator(value);
  }
  return null;
};

const NotificationsCard = ({
  gymData,
  gymDataIsLoading,
  gymDataIsSuccess,
  permission = true,
}: {
  gymData?: GymRetrieve;
  gymDataIsLoading: boolean;
  gymDataIsSuccess: boolean;
  permission?: boolean;
}): JSX.Element => {
  const theme = useMantineTheme();
  const updateMutation = useUpdateGym();

  const [isEditable, { open, close }] = useDisclosure(false);
  const isGymMailConfigUsingMailgun = gymData?.gymMailConfig?.domainProvider === 'mailgun';

  const form = useForm<FormValues>({
    initialValues: {
      gymMailConfig: {
        fromName: null,
        fromEmail: null,
        replyTo: null,
      },
    },

    validate: {
      gymMailConfig: {
        fromName: (value, values) => validateGymMailConfigFromName(value, values),
        fromEmail: (value, values) => {
          if (isGymMailConfigUsingMailgun) {
            return validateGymMailConfigEmail(value, values);
          }
          return null;
        },
        replyTo: (value, values) => {
          const formattedValue =
            value && isGymMailConfigUsingMailgun ? `${value}@${gymData?.gymMailConfig?.domain}` : value;
          return validateGymMailConfigReplyToEmail(formattedValue, values);
        },
      },
    },
  });

  const handleSubmit = (formData: FormValues): void => {
    const { successNotification, errorNotification } = notificationHandler();

    if (gymData) {
      const request = {
        ...gymData,
        gymMailConfig: formData.gymMailConfig,
      };

      updateMutation.mutate(request, {
        onSuccess: response => {
          successNotification('Gym settings update success', `${response.data.title} successfully updated.`);
          close();
        },
        onError: error => {
          errorNotification('Gym settings update failed', error.response?.data.message);
        },
      });
    }
  };

  const setGymNotificationValues = (): void => {
    if (gymData && gymDataIsSuccess) {
      const values = {
        gymMailConfig: isGymMailConfigUsingMailgun
          ? {
              fromName: gymData?.gymMailConfig?.fromName || '',
              fromEmail: gymData?.gymMailConfig?.fromEmail || '',
              replyTo: gymData?.gymMailConfig?.replyTo || '',
            }
          : {
              fromName: gymData?.gymMailConfig?.fromName || '',
              fromEmail: null,
              replyTo: gymData?.gymMailConfig?.replyTo || '',
            },
      };

      form.setValues(values as FormValues);
    }
  };

  const handleCancelChanges = (): void => {
    setGymNotificationValues();
    close();
  };

  useShallowEffect(() => {
    setGymNotificationValues();
  }, [gymData?.gymMailConfig]);

  return (
    <>
      <Paper shadow='xl' p='sm'>
        <LoadingOverlay visible={gymDataIsLoading} />

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Group position='apart' spacing='md' noWrap sx={{ alignItems: 'flex-start' }}>
            <Title order={4}>Email Sender Details</Title>

            <Group spacing='xs'>
              {isEditable ? (
                <>
                  <Button
                    color='red'
                    compact
                    onClick={handleCancelChanges}
                    loading={updateMutation.isLoading}
                  >
                    Cancel
                  </Button>
                  <Button type='submit' color='green' compact loading={updateMutation.isLoading}>
                    Save
                  </Button>
                </>
              ) : (
                <Button
                  compact
                  onClick={open}
                  disabled={!permission}
                  loading={gymDataIsLoading || updateMutation.isLoading}
                >
                  Edit
                </Button>
              )}
            </Group>
          </Group>
          <Divider mx='-sm' my='sm' />
          <Grid align='center' gutter={6} p='xs'>
            <Grid.Col xs={12} md={6}>
              <Stack spacing='xs'>
                <Grid gutter='xs' px='xs' align='center' p='xs'>
                  <Grid.Col span={12}>
                    <Group noWrap grow align='start'>
                      <TextInput
                        label='From Name'
                        disabled={!isEditable}
                        error={form.errors['gymMailConfig.fromName']}
                        value={form.values?.gymMailConfig?.fromName || ''}
                        onChange={e => form.setFieldValue('gymMailConfig.fromName', e?.target?.value)}
                      />
                      {isGymMailConfigUsingMailgun && (
                        <TextInput
                          label='From Email'
                          disabled={!isEditable}
                          error={form.errors['gymMailConfig.fromEmail']}
                          value={form.values?.gymMailConfig?.fromEmail || ''}
                          onChange={e => form.setFieldValue('gymMailConfig.fromEmail', e?.target?.value)}
                        />
                      )}
                      <TextInput
                        label='"Reply to" Email'
                        disabled={!isEditable}
                        error={form.errors['gymMailConfig.replyTo']}
                        value={form.values?.gymMailConfig?.replyTo || ''}
                        onChange={e => {
                          form.setFieldValue('gymMailConfig.replyTo', e?.target?.value);
                        }}
                      />
                      {gymData && isGymMailConfigUsingMailgun && (
                        <Text mt={px(theme.spacing.md) * 2} size='sm' fs='italic' weight='bold' color='gray'>
                          {`@${gymData.gymMailConfig?.domain}`}
                        </Text>
                      )}
                    </Group>
                  </Grid.Col>
                </Grid>
              </Stack>
            </Grid.Col>
          </Grid>
        </form>
      </Paper>
    </>
  );
};

export default NotificationsCard;
